import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import type { TeammatePlaybookCoachingManagerInput } from "@repo/app-types/entities/teammatePlaybookCoachingManagerInput";
import { AlertDialog, AlertDialogVariant } from "@repo/ui/Modal/alertDialog";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { ShortAbsoluteTimestamp } from "@repo/ui/timestamps/shortAbsoluteTimestamp";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { useState, type FC, type ReactNode } from "react";
import {
  usePlaybookTeammateDeleteCoachingManagerInputMutation,
  usePlaybookTeammateSaveCoachingManagerInputMutation,
} from "../../../api/entities/performancePlaybook.js";
import { useCoachingAnalytics } from "../coachingAnalytics.js";
import { ManagerNoteEditor } from "./managerNoteEditor.js";
import { ManagerNoteSurface } from "./managerNoteSurface.js";

export interface ManagerNoteSlotContainerProps {
  playbookId: PlaybookId;
  teammateId: TeammateId;
  /** The coaching period being viewed. */
  periodId: string;
  /** The manager input stored for the viewed period, if any. */
  managerInput: TeammatePlaybookCoachingManagerInput | undefined;
  teammateFirstName: string;
  /** Whether the viewer may edit (manager viewing the current week). */
  canEdit: boolean;
  /** Whether the note editor is open (owned by the coaching section). */
  isEditing: boolean;
  onOpenEditor: () => void;
  onCloseEditor: () => void;
  onDirtyChange: (isDirty: boolean) => void;
  /** Manager actions, shown under the note while the editor is closed. */
  actions?: ReactNode;
}

/**
 * The manager-note slot: the saved note surface (with edit/delete for
 * managers) above the manager actions, or the inline editor while editing.
 */
export const ManagerNoteSlotContainer: FC<ManagerNoteSlotContainerProps> = (
  props,
) => {
  const {
    playbookId,
    teammateId,
    periodId,
    managerInput,
    teammateFirstName,
    canEdit,
    isEditing,
    onOpenEditor,
    onCloseEditor,
    onDirtyChange,
    actions,
  } = props;

  const { captureManagerInputSave, captureManagerInputDelete } =
    useCoachingAnalytics();
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);

  const [saveNote, { isLoading: isSaving }] =
    usePlaybookTeammateSaveCoachingManagerInputMutation();
  const [deleteNote, { isLoading: isDeleting }] =
    usePlaybookTeammateDeleteCoachingManagerInputMutation();

  const note = managerInput?.note ?? null;

  const handleSave = async (newNote: string) => {
    // A stored row counts as an edit even when its note is empty: the manager
    // is replacing something that already existed.
    const isEdit = note !== null;

    try {
      await saveNote({
        playbookId,
        teammateId,
        periodId,
        note: newNote,
      }).unwrap();
    } catch {
      showToast({
        title: "The note could not be saved. Please try again.",
        variant: ToastVariant.Error,
      });
      return;
    }

    onCloseEditor();
    captureManagerInputSave({
      inputType: "note",
      isEdit,
    });
  };

  const handleDelete = async () => {
    try {
      await deleteNote({ playbookId, teammateId, periodId }).unwrap();
    } catch {
      showToast({
        title: "The note could not be deleted. Please try again.",
        variant: ToastVariant.Error,
      });
      return;
    } finally {
      setIsDeleteConfirmationOpen(false);
    }

    captureManagerInputDelete({ inputType: "note" });
  };

  if (isEditing && canEdit) {
    return (
      <ManagerNoteEditor
        initialNote={note ?? ""}
        placeholder={`Leave a note for ${teammateFirstName}...`}
        onCancel={onCloseEditor}
        onSave={(newNote) => void handleSave(newNote)}
        isSaving={isSaving}
        onDirtyChange={onDirtyChange}
      />
    );
  }

  if (!note) {
    return actions ?? null;
  }

  const noteTimestamp = managerInput?.updated_at ?? managerInput?.created_at;

  return (
    <>
      <ManagerNoteSurface
        note={note}
        metadata={
          noteTimestamp ? (
            <ShortAbsoluteTimestamp timestamp={noteTimestamp} />
          ) : undefined
        }
        onEdit={canEdit ? onOpenEditor : undefined}
        onDelete={canEdit ? () => setIsDeleteConfirmationOpen(true) : undefined}
      />
      {actions}

      <CenteredModal
        isOpen={isDeleteConfirmationOpen}
        onOpenChange={setIsDeleteConfirmationOpen}
        isDismissable
      >
        <AlertDialog
          variant={AlertDialogVariant.Warning}
          title="Delete manager note?"
          confirmText="Delete note"
          isConfirming={isDeleting}
          onConfirm={() => void handleDelete()}
        >
          This removes the note from this week&apos;s coaching goal.{" "}
          {teammateFirstName} won&apos;t see it anymore.
        </AlertDialog>
      </CenteredModal>
    </>
  );
};
